import { z } from "zod";
//#region src/pod-phase.ts
/**
* ⭐ **THE K8S POD-PHASE VOCABULARY — the ONE definition, in the LEAF package.**
*
* ## ⛔ Why this module exists (#1994)
*
* Three wire schemas carried a `phase` field typed `z.string()` under a docblock that ENUMERATED
* the five k8s values, and one of them also carried `.min(1)`. A grammar stated in prose and
* enforced as "any string" is not a contract: `phase: 'Nonsense'` parsed clean, and
* `cp-admin-ui`'s own conformance suite recorded that discovery in situ
* (`panes/__tests__/fixture-conformance.test.ts`) — *"A control written against a docblock rather
* than against the schema is a control that cannot fail."*
*
* ## ⛔ IT IS A TOLERANT VIEW, NOT A BARE ENUM, AND THAT IS THE WHOLE RULING
*
* A bare `z.enum(POD_PHASES)` would TIGHTEN a wire contract. The kubelet is not our code and its
* value set is not ours to bet on: a sixth `status.phase` would newly REJECT the whole pod on a
* read path that today degrades gracefully. {@link podPhaseView} therefore accepts any string and
* maps an unrecognised one to `'Unknown'` — k8s's OWN name for "we do not know" — so a future
* kubelet costs us one under-described pod row instead of a 500.
*
* ⛔ **AND IT IS NOT `z.enum(POD_PHASES).catch('Unknown')`, WHICH READS SAFER AND IS NOT.**
* MEASURED on zod 4.4.3: `.catch` also swallows a MISSING key —
* `z.object({ phase: z.enum(P).catch('Unknown') }).strict().safeParse({})` SUCCEEDS and returns
* `{ phase: 'Unknown' }` — and `z.toJSONSchema(..., { io: 'input' })` drops `phase` from
* `required`. That makes "the producer stopped emitting the field" indistinguishable from "the
* producer reported Unknown", which are different facts. The `z.string()` head is what keeps a
* missing key, a `null` and a non-string REJECTED, exactly as before.
*
* ⛔ **AND THE `.pipe()` IS NOT DECORATION.** A bare `.transform` is UNREPRESENTABLE in JSON Schema
* — MEASURED: `z.toJSONSchema(z.string().transform(f))` throws *"Transforms cannot be represented
* in JSON Schema"*, and at object level the whole document fails to emit. `site-admin-api` builds
* an OpenAPI document at boot (`src/main.ts:62`), so an unrepresentable response schema is a boot
* risk. Piped into the enum, the output schema emits the full five-value enum and the input schema
* emits `type: 'string'`. Both directions verified.
*
* ## ⛔ Why the vocabulary lives HERE and not in `scaling-event.ts`
*
* `POD_LIFECYCLE_PHASES` was declared in `scaling-event.ts`, which is the correct home for
* autoscaling telemetry and the wrong home for a vocabulary three pod-read modules need. Rather
* than import upward from a telemetry module, the vocabulary moves DOWN here and
* `scaling-event.ts` re-exports its existing names; every consumer of those names is untouched and
* there is still exactly one definition. This is the `id-grammar.ts` pattern, applied a second
* time for the same reason.
*
* ## ⛔ `normalizePodPhase` WAS ALREADY WRITTEN — this is a promotion, not an invention
*
* `zion-operator/src/reconciler/scaling-event-reconciler.ts` carried a byte-equivalent private
* `toPodPhase`, including the `.find()` idiom that avoids a cast. It is deleted there and imported
* from here, so the producer and the schema can never disagree about what an unknown phase maps to.
*/
/**
* The five values `PodStatus.phase` can hold, in k8s's own order.
*
* ⛔ `'Unknown'` is a REAL k8s phase, not a sentinel this repo invented. That is why it is safe to
* use as the fallback: the fallback value is inside the vocabulary, so a consumer that switches
* exhaustively over these five needs no extra arm.
*
* ⚠️ Order is declaration order and nothing depends on it today. Should anything ever snapshot it,
* APPEND — never insert.
*/
const POD_PHASES = [
	"Pending",
	"Running",
	"Succeeded",
	"Failed",
	"Unknown"
];
/** The strict enum. ⛔ Bind {@link podPhaseView} on a wire schema, never this. */
const podPhaseEnum = z.enum(POD_PHASES);
/**
* Map a raw k8s phase onto {@link PodPhase}, degrading anything unrecognised to `'Unknown'`.
*
* ⛔ `undefined` is accepted and maps to `'Unknown'` so a producer reading an optional
* `status.phase` off an untrusted payload can call this directly without a guard.
*
* ⚠️ `.find()` on the const tuple returns `PodPhase | undefined` with NO cast. A cast would let a
* later edit widen the tuple's element type without the compiler noticing.
*/
function normalizePodPhase(raw) {
	return POD_PHASES.find((p) => p === raw) ?? "Unknown";
}
/**
* The wire view: accepts any string, emits a {@link PodPhase}.
*
* ⛔ Bind THIS on a wire schema. A missing key, a `null` and a non-string are still rejected; only
* an unrecognised STRING degrades. See this module's header for the three spellings that were
* measured and rejected.
*/
const podPhaseView = z.string().transform(normalizePodPhase).pipe(podPhaseEnum);
//#endregion
export { POD_PHASES, normalizePodPhase, podPhaseEnum, podPhaseView };

//# sourceMappingURL=pod-phase.js.map